import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "semantic-ui-react";

import "lib/Form/components/FormFileDragDrop/FormFileDragDrop.scss";
import { MAX_UPLOAD_FILE_SIZE_BYTES } from "utils/common";

import type { FieldValues, Path } from "react-hook-form";

type ShallowPath<T> = keyof T & string;
type DragEvent = React.DragEvent<HTMLDivElement>;

interface FormFileDragDropProps<T extends FieldValues> {
  name: ShallowPath<T>;
  id: string;
  required?: boolean;
  disabled?: boolean;
  isUploading?: boolean;
  onChange?: (file: File) => void;
}

//TODO update to support upload of multiple files
export const FormFileDragDrop = <T extends FieldValues>({ onChange, name, id, required = false, disabled = false, isUploading = false }: FormFileDragDropProps<T>) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const [draggedOver, setDraggedOver] = useState(false);

  const validate = (file: File | null) => {
    if (required && !file) return t("v8_files_are_required", "Files are required");
    if (file && file.size > MAX_UPLOAD_FILE_SIZE_BYTES) return t("v8_max_file_size_is_32MB", "Max file size is 32MB");

    return true;
  };
  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) setDraggedOver(true);
  };
  const handleDragOver = (e: DragEvent) => !disabled && !isUploading && e.preventDefault();
  const handleDragLeave = () => !disabled && !isUploading && setDraggedOver(false);

  return (
    <Controller
      name={name as Path<T>}
      control={control}
      rules={{ validate }}
      render={({ field, fieldState }) => {
        const handleFiles = (files: FileList | null) => {
          if (!files || disabled || isUploading) return;

          field.onChange(files[0]);
          onChange?.(files[0]);
        };

        return (
          <div
            className={`FormFileDragDrop ${draggedOver ? "active" : ""} ${fieldState.error ? "error" : ""} ${isUploading ? "uploading" : ""}`}
            onDrop={(e: DragEvent) => {
              e.preventDefault();
              setDraggedOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="form-file-drag-drop-content">
              {isUploading ? (
                <span className="uploading-text">{t("v8_uploading", "Uploading")}</span>
              ) : (
                <>
                  <span>
                    {t("v8_drag_files_here", "Drag files here")} {t("v8_or", "or")}
                  </span>
                  <span className="browse-text" onClick={() => !disabled && document.getElementById(id)?.click()}>
                    {t("v8_browse", "Browse")}
                  </span>
                </>
              )}
            </div>

            <div className="form-file-drag-drop-max-file-size">{t("v8_maximum_file_size", "Maximum file size")} 32MB</div>

            <Input
              className="form-file-drag-drop-input"
              id={id}
              name={name}
              type="file"
              hidden
              disabled={disabled || isUploading}
              onChange={e => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
        );
      }}
    />
  );
};
